"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Send } from "lucide-react";
import type { QuickBooksInvoice } from "@/lib/types/quickbooks-unified";
import { PrintStatusBadge, EmailStatusBadge } from "./InvoiceBadges";
import { formatDateTime } from "../utils/invoiceFormatters";

interface InvoiceStatusTabProps {
  invoice?: QuickBooksInvoice;
}

/**
 * Status & Delivery tab component for invoice details
 * Displays print status, email status, and delivery information
 */
export const InvoiceStatusTab: React.FC<InvoiceStatusTabProps> = ({ invoice }) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Invoice Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 gap-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="text-xs font-medium text-gray-600 mb-1">Print Status</div>
                <div>
                  <PrintStatusBadge status={invoice?.PrintStatus} />
                </div>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <div className="text-xs font-medium text-gray-600 mb-1">Email Status</div>
                <div>
                  <EmailStatusBadge status={invoice?.EmailStatus} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="h-4 w-4" />
              Delivery Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 gap-3">
              <div className="p-3 bg-purple-50 rounded-lg">
                <div className="text-xs font-medium text-gray-600 mb-1">Delivery Method</div>
                <div className="text-base font-semibold">
                  {invoice?.DeliveryInfo?.DeliveryType || "N/A"}
                </div>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <div className="text-xs font-medium text-gray-600 mb-1">Delivery Time</div>
                <div className="text-base font-semibold">
                  {invoice?.DeliveryInfo?.DeliveryTime
                    ? formatDateTime(invoice.DeliveryInfo.DeliveryTime)
                    : "N/A"}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
